import { useSyncStore } from './sync-store';
import { performSync } from './sync-engine';
import { refreshSyncCounts } from './sync-counts';
import { checkApiHealth } from '../../api/client';

let healthInterval: ReturnType<typeof setInterval> | null = null;
let initialized = false;

export const HEALTH_POLL_INTERVAL_MS = 30_000;

/**
 * Consecutive failed probes required before declaring a dead zone.
 *
 * Only applies when `navigator.onLine` is true — i.e. the radio claims to be
 * up but nothing answers. That is the flaky case (a single dropped packet on a
 * rig road is not a dead zone), so one blip should not tear the app into
 * offline mode. The genuinely-offline case does not wait: `navigator.onLine
 * === false` short-circuits to offline immediately, below.
 */
export const FAILED_PROBES_BEFORE_OFFLINE = 2;

/**
 * Timed-out probes required before declaring a dead zone.
 *
 * Higher than the rejection threshold on purpose. A dead network *rejects* —
 * it does not take 8 seconds to do it — so this longer fuse costs a real
 * dead zone nothing. What it buys is the false positive it was raised for: an
 * API cold start, a rig-road link at one bar, or a tab the browser throttled
 * while it was in the background, none of which mean the user's work has
 * stopped syncing.
 */
export const TIMED_OUT_PROBES_BEFORE_OFFLINE = 3;

/** Reset only by a probe the API actually answered — see `probeOnline`. */
let consecutiveFailures = 0;
let consecutiveTimeouts = 0;

/**
 * Which path last moved `isOnline`. Permanent instrumentation: when the banner
 * is wrong again, this is the difference between reading it off the console and
 * guessing. Logged at every `setOnline` call and nowhere else, so the poll stays
 * silent for as long as nothing changes.
 */
type OnlinePath =
  | 'seed'
  | 'probe-success'
  | 'probe-error'
  | 'probe-timeout'
  | 'dom-offline-event';

/**
 * `online: null` — "no verdict" — is the point of this type.
 *
 * A probe that did not get an answer is not evidence of *either* state. It may
 * tear the app offline only once it has accumulated enough of itself, and until
 * then it says nothing rather than voting to stay online. It must never be able
 * to assert online at all, or a single timeout arriving while the banner is up
 * would clear it without the API having answered anything.
 */
type ProbeVerdict = { online: boolean | null; path: OnlinePath };

/**
 * Is the network actually usable right now?
 *
 * `navigator.onLine === false` is authoritative. The browser is telling us
 * there is no interface at all, and no probe can contradict that — this is the
 * short-circuit that makes it *impossible* for the poll to flip `isOnline`
 * back to true in a dead zone, which is what the old relative-`/health` poll
 * did every 30 seconds.
 *
 * `navigator.onLine === true` is only a hint (it stays true on a captive
 * portal, on a radio with no route, at one bar with nothing getting through)
 * — which is exactly the mobile case the original comment said the poll was
 * for, and exactly the case it could not serve. So we ask the API.
 *
 * Neither counter is reset by the *other* kind of failure. A link alternating
 * between rejecting and timing out is not healthy, and clearing one count with
 * the other is how it would stay "online" through all of it. Only an answer
 * from the API clears them.
 */
async function probeOnline(): Promise<ProbeVerdict> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    consecutiveFailures = FAILED_PROBES_BEFORE_OFFLINE;
    return { online: false, path: 'dom-offline-event' };
  }

  const result = await checkApiHealth();
  // Every probe reports whether the API answered, verdict or not — the bar's
  // "Synced …" line reads this, and must not wait for the dead-zone threshold.
  useSyncStore.getState().setServerUnreachable(result !== 'ok');

  if (result === 'ok') {
    consecutiveFailures = 0;
    consecutiveTimeouts = 0;
    return { online: true, path: 'probe-success' };
  }

  if (result === 'timeout') {
    consecutiveTimeouts += 1;
    return {
      online: consecutiveTimeouts >= TIMED_OUT_PROBES_BEFORE_OFFLINE ? false : null,
      path: 'probe-timeout',
    };
  }

  consecutiveFailures += 1;
  return {
    online: consecutiveFailures >= FAILED_PROBES_BEFORE_OFFLINE ? false : null,
    path: 'probe-error',
  };
}

/**
 * Probe now, and move `isOnline` only if the probe reached a verdict that
 * differs from where we already are.
 *
 * Every trigger — the interval, both DOM events, tab refocus, startup — comes
 * through here. There is exactly one place that decides, so a trigger cannot
 * assert a state on its own; it can only ask.
 */
async function probeAndApply(): Promise<void> {
  const { online, path } = await probeOnline();
  if (online === null) return;
  if (online === useSyncStore.getState().isOnline) return;
  await handleOnlineChange(online, path);
}

/** Every `setOnline` in this module goes through here first. */
function logOnlineTransition(online: boolean, path: OnlinePath) {
  console.info(
    '[online-detector] isOnline=%s via %s (failures=%d, timeouts=%d)',
    online,
    path,
    consecutiveFailures,
    consecutiveTimeouts
  );
}

async function handleOnlineChange(online: boolean, path: OnlinePath) {
  const store = useSyncStore.getState();
  logOnlineTransition(online, path);
  store.setOnline(online);

  await refreshSyncCounts();

  // Auto-sync on reconnect
  if (online && !store.isSyncing) {
    store.setSyncing(true);
    try {
      const result = await performSync();
      // Only ever a client clock for the "Synced Xm ago" label. The server
      // cursor is written inside performSync from server time — see the note
      // there on why these are two different timestamps.
      store.setLastSyncAt(result.timestamp);
      store.setConflicts(result.pushed.conflicts);
      store.setError(null);
      store.setServerUnreachable(false);
    } catch (err: any) {
      store.setError(err.message || 'Sync failed');
    } finally {
      store.setSyncing(false);
      await refreshSyncCounts();
    }
  }
}

const handleDomOnline = () => {
  // The browser regaining an interface is a hint, not proof — confirm with
  // the API before telling the user their work is syncing again. It does not
  // clear `consecutiveFailures` on the way in: an unproven hint is not an
  // answer from the API, and treating it as one is how a captive portal used
  // to clear the banner.
  void probeAndApply();
};

const handleDomOffline = () => {
  // Deliberately does NOT set offline. The event is a hint like its opposite,
  // and an unconfirmed one raised the banner on a device that was online the
  // whole time. A real disconnect still lands within this single probe:
  // `navigator.onLine === false` short-circuits it. A flap that has already
  // recovered by the time we ask does not.
  void probeAndApply();
};

const handleVisibilityChange = () => {
  // A backgrounded tab has its timers throttled, so the 30s poll may not have
  // run for minutes and `isOnline` can be arbitrarily stale by the time someone
  // looks at the screen again. Ask before they read the banner, not after.
  if (document.visibilityState === 'visible') void probeAndApply();
};

export function initOnlineDetection() {
  if (initialized) return;
  initialized = true;

  window.addEventListener('online', handleDomOnline);
  window.addEventListener('offline', handleDomOffline);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Poll the API every 30s (navigator.onLine is unreliable on mobile).
  healthInterval = setInterval(() => {
    void probeAndApply();
  }, HEALTH_POLL_INTERVAL_MS);

  // Seed from the browser, then correct it immediately. A device launched in a
  // dead zone reports `navigator.onLine === true` on iOS often enough that
  // waiting a full poll interval to find out is its own small lie.
  logOnlineTransition(navigator.onLine, 'seed');
  useSyncStore.getState().setOnline(navigator.onLine);
  void probeAndApply();
}

export function destroyOnlineDetection() {
  if (healthInterval) {
    clearInterval(healthInterval);
    healthInterval = null;
  }
  // Listeners are removed, not left behind. They were leaking before: `initialized`
  // was cleared without detaching anything, so a second `init` in the same
  // document ran every handler twice over shared module state.
  window.removeEventListener('online', handleDomOnline);
  window.removeEventListener('offline', handleDomOffline);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  consecutiveFailures = 0;
  consecutiveTimeouts = 0;
  initialized = false;
}
